import { useLocation, useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

import { usePostModalParams } from "@/react/hooks/usePostModalParams";

const POST_PAGE_PATH = "/c/$spaceSlug/$postSlug" as const;

export const useIsPostModalActive = () =>
  useLocation({
    select: location => !!location.maskedLocation,
  });

export const usePostModalBackgroundPathname = () =>
  useLocation({
    select: location => {
      if (!location.maskedLocation) return undefined;
      return location.pathname;
    },
  });

export const useReplaceLocationState = () => {
  const navigate = useNavigate();
  const isModalActive = useIsPostModalActive();
  const { spaceSlug, postSlug } = usePostModalParams();
  const hash = useLocation({
    select: location => location.maskedLocation?.hash ?? location.hash ?? "",
  });

  return useCallback(
    (
      statePatch: Record<string, unknown>,
      { hash: hashOverride }: { hash?: string } = {},
    ) => {
      const nextHash = hashOverride ?? hash;

      if (isModalActive && spaceSlug && postSlug) {
        void navigate({
          to: ".",
          unsafeRelative: "path",
          replace: true,
          resetScroll: false,
          search: true,
          state: prev => ({ ...prev, ...statePatch }),
          mask: {
            to: POST_PAGE_PATH,
            params: { spaceSlug, postSlug },
            hash: nextHash,
            unmaskOnReload: true,
          },
        });
        return;
      }

      void navigate({
        to: ".",
        replace: true,
        search: true,
        hash: nextHash,
        state: prev => ({ ...prev, ...statePatch }),
      });
    },
    [navigate, isModalActive, spaceSlug, postSlug, hash],
  );
};

export const useClosePostModal = () => {
  const navigate = useNavigate();
  const isModalActive = useIsPostModalActive();

  return useCallback(() => {
    if (!isModalActive) return;

    void navigate({
      to: ".",
      // User opened the post from /feed (or a space page) — the app is still on
      // that page; only the URL bar shows /c/space/post. Close = stay on /feed
      // and reset the URL. unsafeRelative tells TSR to use /feed, not the mask.
      unsafeRelative: "path",
      search: true,
      replace: true,
      resetScroll: false,
    });
  }, [navigate, isModalActive]);
};

export const useExpandPostModalToFullPage = () => {
  const navigate = useNavigate();
  const hash = useLocation({
    select: location => location.maskedLocation?.hash ?? location.hash ?? "",
  });

  return useCallback(
    (post: { slug: string; space_slug: string }) => {
      void navigate({
        to: "/c/$spaceSlug/$postSlug",
        params: {
          spaceSlug: post.space_slug,
          postSlug: post.slug,
        },
        hash,
        state: { isRedirectFromPostModal: true } as Record<string, unknown>,
      });
    },
    [navigate, hash],
  );
};
